import { TelehealthCta } from "@/components/site/TelehealthCta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { canonicalAndAlternates } from "@/lib/hreflang";
import lapBandDiagram from "@/assets/lap-band-diagram.jpg.asset.json";
import { bandDiagramResponsive } from "@/lib/service-responsive";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { InlineAppointmentCTA } from "@/components/site/InlineAppointmentCTA";
import { ProcedureFaq } from "@/components/site/ProcedureFaq";
import { faqSchema, type Faq } from "@/components/site/InsuranceProse";
import { medicalWebPageScript, imageRights, PROCEDURE_ACTORS, SETTING_OUTPATIENT } from "@/lib/medical-schema";

const BAND_URL = "https://virginiabariatric.com/services/band";

const IMAGE_RIGHTS = imageRights(BAND_URL);

const BAND_IMAGE_ALT =
  "Medical illustration of an adjustable gastric band (LAP-BAND) placed around the top of the stomach with its tubing and fill port";

const BAND_OG_IMAGE = `https://virginiabariatric.com${lapBandDiagram.url}`;

const BAND_FAQS: Faq[] = [
  {
    q: "How much weight do patients lose with the LAP-BAND?",
    a: "About 45% of excess weight on average, against roughly 70% for the gastric sleeve. The adjustable gastric band — sometimes called a stomach band or \u201cgastric belt\u201d — depends heavily on regular adjustments and long-term follow-up.",
  },
  {
    q: "Is LAP-BAND surgery reversible?",
    a: "Yes. The band is the only weight loss surgery that does not permanently alter your gastric tract, so it can be removed laparoscopically if needed.",
  },
  {
    q: "How often does the band need to be adjusted?",
    a: "For life. Expect a visit about every six weeks in the first year, and less often after that.",
  },
  {
    q: "How long does LAP-BAND surgery take and when can I go home?",
    a: "The operation usually takes about 30 minutes, and most patients go home the same day.",
  },
  {
    q: "Can a gastric band be converted to another procedure?",
    a: "Yes. Conversion to a sleeve gastrectomy or gastric bypass is common and well studied. It can be done in one operation or staged as two, depending on your anatomy. For patients who already have a sleeve rather than a band, a re-sleeve (also spelled resleeve) is a separate revision option we review on our revision surgery page.",
  },
  {
    q: "Is the LAP-BAND covered by insurance?",
    a: "Many insurers cover adjustable gastric banding when medical criteria are met, such as a BMI of 35 or higher, or 30 or higher with an obesity-related condition. Our team verifies benefits and handles authorization.",
  },
];

const META_DESCRIPTION =
  "Gastric band surgery (LAP-BAND®) places an adjustable band around the top of the stomach — the least invasive, only reversible weight loss operation.";

export const Route = createFileRoute("/services/band")({

  head: () => ({
    meta: [
      { title: "LAP-BAND® Surgery in Northern Virginia | Virginia Bariatric" },
      { name: "description", content: META_DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "LAP-BAND® Surgery in Northern Virginia | Virginia Bariatric" },
        { property: "og:description", content: META_DESCRIPTION },
        { property: "og:url", content: "https://virginiabariatric.com/services/band" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: BAND_OG_IMAGE },
        { property: "og:image:alt", content: BAND_IMAGE_ALT },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: BAND_OG_IMAGE },
        { name: "twitter:image:alt", content: BAND_IMAGE_ALT },
        { name: "twitter:title", content: "LAP-BAND® Surgery in Northern Virginia | Virginia Bariatric" },
        { name: "twitter:description", content: META_DESCRIPTION },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/services/band") : []),
    ],
    scripts: import.meta.env.SSR ? [
      ...medicalWebPageScript("/services/band"),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalProcedure",
          "@id": `${BAND_URL}#procedure`,
          mainEntityOfPage: { "@id": `${BAND_URL}#webpage` },
          procedureType: "https://schema.org/SurgicalProcedure",
          name: 'Adjustable Gastric Banding (LAP-BAND)',
          description:
            "The least invasive surgical weight loss option, and the only one that can be fully reversed.",
          url: "https://virginiabariatric.com/services/band",
          bodyLocation: "Stomach",
          howPerformed:
            "Performed laparoscopically under general anesthesia, an adjustable silicone band is placed around the upper stomach to create a small pouch. The band is connected to a port under the skin so its tightness can be adjusted in the office. " + SETTING_OUTPATIENT,
          preparation: "Preoperative medical and nutritional evaluation, laboratory testing, and a pre-surgery diet.",
          followup: "Regular office visits for band adjustments plus long-term nutrition and weight-management follow-up.",
          image: [
            {
              "@type": "ImageObject",
              "@id": `${BAND_URL}#primaryimage`,
              contentUrl: BAND_OG_IMAGE,
              url: BAND_OG_IMAGE,
              caption: BAND_IMAGE_ALT,
              name: "Adjustable gastric band (LAP-BAND) anatomy diagram",
              description:
                "Medical illustration showing an adjustable gastric band around the top of the stomach, creating a small pouch above the band, with tubing leading to the fill port.",
              representativeOfPage: true,
              width: 900,
              height: 1055,
              inLanguage: "en-US",
              ...IMAGE_RIGHTS,
            },
          ],
          ...PROCEDURE_ACTORS,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqSchema(BAND_FAQS, BAND_URL)),
      },
    ] : [],
  }),
  component: BandPage,
});


function BandPage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif text-center">LAP-BAND® Adjustable Gastric Banding in Northern Virginia</h1>
      </header>

      <p className="mt-6 text-foreground/80">
        Gastric band surgery places an adjustable silicone band around the upper part of the stomach
        to reduce how much you eat at one sitting. Unlike other operations, no part of the stomach
        is stapled or removed. It is the least invasive of the surgical weight loss options and the
        only one that can be fully reversed.
      </p>

      <TelehealthCta />

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">What is the LAP-BAND?</h2>
      <p className="mt-4 text-foreground/80">
        An adjustable gastric band is a plastic ring about an inch and a half across, close to the
        size of a napkin ring. Thin tubing connects it to a port fixed to the abdominal wall near
        the belly button. The port stays invisible under the skin, even after major weight loss.
        Your provider can reach it with a small needle when it’s time to adjust the band.
      </p>
      <p className="mt-4 text-foreground/80">
        It takes a surgeon about 30 minutes to put it in, and most patients go home the same day.
        Nothing is stapled, cut, or removed, so your anatomy stays more or less intact. The band can
        be taken out later if you choose.
      </p>
      <p className="mt-4 text-foreground/80">
        Because the small intestine is not touched, the band does not affect how you absorb
        nutrients.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        How the LAP-BAND helps you lose weight
      </h2>
      <p className="mt-4 text-foreground/80">
        The band’s tightness creates a small stomach pouch above it, roughly the size of a walnut:
      </p>

      <figure className="mt-6 flex flex-col items-center">
        <picture>
          <source srcSet={bandDiagramResponsive.srcSet} sizes="(min-width: 768px) 320px, 70vw" type="image/webp" />
          <img
            src={lapBandDiagram.url}
            alt={BAND_IMAGE_ALT}
            width={900}
            height={1055}
            loading="lazy"
            decoding="async"
            sizes="(min-width: 768px) 320px, 70vw"
            className="w-full max-w-[20rem] h-auto rounded-lg border border-border"
          />
        </picture>
        <figcaption className="mt-2 text-center text-sm text-foreground/70">
          An adjustable gastric band in place, with the small pouch above the band and the fill port
          under the skin
        </figcaption>
      </figure>

      <p className="mt-6 text-foreground/80">
        Food you swallow into your pouch creates pressure. Before long, that pressure forces the
        bite of chewed food forward — it is literally squeezed out of the pouch, like toothpaste
        from a tube. The exit of the pouch is an opening about the width of your little finger.
      </p>
      <p className="mt-4 text-foreground/80">
        The pressure created when the esophagus pushes food into the pouch is either a little or a
        whole lot, depending on how fast you eat. Those two experiences are very different for
        patients. Both can lead to weight loss, but the gentle-pressure way is far better.
      </p>

      <InlineAppointmentCTA
        heading="Wondering if the LAP-BAND fits your situation?"
        body="Band, sleeve, or bypass — the right choice depends on your anatomy, goals, and willingness to commit to long-term follow-up. Talk it through with Dr. Fitzer."
        buttonText="Schedule an Appointment to discuss LAP-BAND surgery"
        secondaryButton={{ text: "Explore Band Revision Surgery", to: "/services/revisions/lap-band" }}
      />

      <h3 className="mt-8 text-[1.1875rem] md:text-[1.375rem] font-serif">
        Gentle pressure above the LAP-BAND
      </h3>
      <p className="mt-4 text-foreground/80">
        If you eat carefully — small bites, waiting until the last one has passed through before
        taking another — you get just a little pouch pressure. A little pressure is not
        uncomfortable. The only thing you notice, if anything, is the faint sensation that you could
        maybe burp if you tried.
      </p>
      <p className="mt-4 text-foreground/80">
        Light pressure pays off. Let it sit, and it activates stomach nerves that signal fullness to
        your brain. Eating was pleasant, and that small meal left you very satisfied.
      </p>

      <h3 className="mt-8 text-[1.1875rem] md:text-[1.375rem] font-serif">
        A lot of pressure above the LAP-BAND
      </h3>
      <p className="mt-4 text-foreground/80">
        If you eat quickly, you generate high pouch pressures, and high pressures are unpleasant.
        It’s like a lump of bread stuck in your throat, only worse. You may have discomfort or pain
        under your breastbone. You may feel provoked to vomit. High pressure also causes a
        surprising amount of saliva to come out all at once. When a band patient eats too fast and
        suddenly has a mouth full of saliva they are nervous about swallowing, we call it "having
        the foamies."
      </p>
      <p className="mt-4 text-foreground/80">
        Eating fast does lead to weight loss — in the short term. High-pressure episodes cause band
        patients to lose interest in eating, sometimes for the rest of the day, sometimes for
        several days if the episode was bad. All that meal skipping takes weight off.
      </p>
      <p className="mt-4 text-foreground/80">
        Eventually the high-pressure method stops working. Some patients develop an aversion to the
        foods that caused it. Most patients who experience high pressures over and over will
        eventually develop a band prolapse, or "slip," a complication that usually means the band
        must come out.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Adjustments and follow-up</h2>
      <p className="mt-4 text-foreground/80">
        An adjustable gastric band needs to be tightened or loosened now and then — for life. Early
        on you visit the band clinic about every six weeks. Later the visits are sparser, but they
        do not stop entirely.
      </p>
      <p className="mt-4 text-foreground/80">
        Patients tend to underestimate the importance of having a good band manager. Whoever manages
        your fills — surgeon, nurse, or physician assistant — influences your result more than
        whoever placed the band. If you are shopping programs, ask who performs the adjustments and
        how often you will be seen.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        How much weight do patients lose with the band?
      </h2>
      <p className="mt-4 text-foreground/80">
        Excess weight loss with the adjustable gastric band averages around 45%. The{" "}
        <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">
          gastric sleeve
        </Link>{" "}
        averages roughly 70%, the{" "}
        <Link to="/services/bypass" className="text-brand-deep underline hover:no-underline">
          gastric bypass
        </Link>{" "}
        slightly more.
      </p>
      <p className="mt-4 text-foreground/80">
        Band results vary more widely than other procedures, and the difference usually comes down
        to follow-up. Patients who keep up with their adjustments do considerably better than those
        who drift away after the first year.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Advantages and trade-offs</h2>
      <p className="mt-4 text-foreground/80">
        Every weight loss surgery involves trade-offs. Here is where the band sits.
      </p>
      <p className="mt-4 text-foreground/80">
        In its favor: it is the least invasive surgical option, the only reversible one, usually an
        outpatient procedure, and it leaves nutrient absorption alone. Complications around the time
        of surgery are uncommon.
      </p>
      <p className="mt-4 text-foreground/80">
        On the other side: average weight loss is lower than with the sleeve or bypass, adjustments
        and office visits continue indefinitely, and further procedures to adjust, replace, or
        remove the band are more common than with other operations. Use has also declined sharply in
        the United States, from roughly a third of all bariatric procedures in 2011 to well under
        one percent in 2023.
      </p>
      <p className="mt-4 text-foreground/80">
        Whether those trade-offs work in your favor depends on your anatomy, your goals, and how
        much follow-up suits your life.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">If you already have a LAP-BAND</h2>
      <p className="mt-4 text-foreground/80">
        Many people are living with a band placed years ago. If yours has stopped working well, you
        have options.
      </p>
      <p className="mt-4 text-foreground/80">
        Bands can be adjusted, and they can be removed laparoscopically. Many patients have the band
        taken out and converted to a sleeve gastrectomy or a gastric bypass, in one operation or
        two. Gastric band removal and conversion are well studied and, in experienced hands, safe.
        Our{" "}
        <Link to="/services/revisions/lap-band" className="text-brand-deep underline hover:no-underline">
          LAP-BAND revision page
        </Link>{" "}
        explains that path.
      </p>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/services/revisions/lap-band"
          className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90 text-center"
        >
          Explore band revision surgery
        </Link>
        <Link
          to="/services/revisions/lap-band"
          className="rounded-full border border-brand-deep px-7 py-3 text-sm font-medium text-brand-deep hover:bg-brand-deep/5 text-center"
        >
          LAP-BAND removal &amp; conversion
        </Link>
      </div>


      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">LAP-BAND surgery and adjustments in Northern Virginia</h2>
      <p className="mt-3 text-foreground/80">LAP-BAND placement is almost always outpatient at INOVA Fair Oaks Hospital: most patients go home the same day. Band adjustments are done in our Herndon office, so being nearby matters. Patients in Herndon, Reston, Fairfax and Loudoun County can fit adjustments into a normal day.</p>
      <p className="mt-3 text-foreground/80">Comparing options? See <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery in Northern Virginia</Link> and <Link to="/services/bypass" className="text-brand-deep underline hover:no-underline">gastric bypass in Northern Virginia</Link>. If you already have a band, see <Link to="/services/revisions/lap-band" className="text-brand-deep underline hover:no-underline">LAP-BAND removal and revision in Virginia</Link>.</p>

      <ProcedureFaq heading="LAP-BAND surgery: frequently asked questions" faqs={BAND_FAQS} />

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Let's talk about it.</h2>
      <p className="mt-4 text-foreground/80">
        Choosing among the band, the sleeve, and the bypass is not a decision to make from a
        website. Each suits a different patient. Request a consultation and we will go through all
        of your options with you. If you are thinking about paying for the operation yourself, our{" "}
        <Link to="/self-pay-bariatric-surgery" className="text-brand-deep underline hover:no-underline">
          self-pay bariatric surgery
        </Link>{" "}
        page lists our published prices for every procedure we offer.
      </p>
      <p className="mt-3 text-foreground/80">
        Not using insurance? See the{" "}
        <Link to="/self-pay-bariatric-surgery" hash="lap-band-cost" className="text-brand-deep underline hover:no-underline">
          Lap-Band cost
        </Link>{" "}
        self-pay: an estimated $19,200, which covers the surgeon fee, one hospital day at INOVA Fair
        Oaks Hospital, an anesthesia estimate, and a year of routine follow-up with Dr. Fitzer. We
        also explain the{" "}
        <Link to="/resources/prosper" className="text-brand-deep underline hover:no-underline">
          financing options
        </Link>{" "}
        available.
      </p>


      <div className="mt-6 flex flex-wrap justify-center gap-3">

        <Link to="/appointments" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
          Request a consultation
        </Link>
        <Link
          to="/services/revisions/lap-band"
          className="rounded-full border border-brand-deep px-7 py-3 text-sm font-medium text-brand-deep hover:bg-brand-deep/5 text-center"
        >
          Band revision surgery
        </Link>
        <WeightLossOptionsButtons />
      </div>
      <LastUpdated className="mt-10" />
      </article>
  );
}
